import { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import type { SlideProps } from '@/components/SlideWizard'
import iceburgImage from '@/assets/iceburg.png'
import { useSlideCopy } from '@/lib/i18n'
import { copy } from './Slide11EmotionalToll.copy'

const ease = [0.16, 1, 0.3, 1] as const

// Entry timeline:
//   0.0s         :  Title + intro visible immediately.
//   0.1s  – 1.3s:  Chip counts up quietly in gray. Stays visible at its final
//                  value for the rest of the slide.
//   0.2s  – 0.8s:  Iceberg, water gradient, and cards fade in together.
const COUNT_START_MS = 100
const COUNT_DURATION_MS = 1200
const FADE_IN_DELAY_S = 0.2
const FADE_IN_DURATION_S = 0.6

// Shared fade-in for everything that lands after the count-up.
const enterProps = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
} as const
const enterTransition = {
  duration: FADE_IN_DURATION_S,
  delay: FADE_IN_DELAY_S,
  ease,
}

function useCountUp(target: number, durationMs: number, startAfterMs: number) {
  const [value, setValue] = useState(0)

  useEffect(() => {
    let rafId = 0
    let startTime = 0

    const tick = (now: number) => {
      if (!startTime) startTime = now
      const elapsed = now - startTime
      const progress = Math.min(1, elapsed / durationMs)
      const eased = 1 - Math.pow(1 - progress, 3)
      setValue(Math.round(target * eased))
      if (progress < 1) rafId = requestAnimationFrame(tick)
    }

    const timer = setTimeout(() => {
      rafId = requestAnimationFrame(tick)
    }, startAfterMs)

    return () => {
      clearTimeout(timer)
      cancelAnimationFrame(rafId)
    }
  }, [target, durationMs, startAfterMs])

  return value
}

export default function Slide11EmotionalToll(_props: SlideProps) {
  const c = useSlideCopy(copy)
  const countedAmount = useCountUp(c.tipAmount, COUNT_DURATION_MS, COUNT_START_MS)
  // en-US grouping keeps Western digits and the comma separator on every
  // face, including Arabic-locale browsers (Arabic-Indic digits are out of
  // style for this tenant). Wrapped in an LTR isolate below so the leading
  // minus stays in front of the figure under RTL.
  const tipNumber = `${c.tipPrefix}${countedAmount.toLocaleString('en-US')}`
  const tipDisplay = (
    <>
      <span dir="ltr">{tipNumber}</span>
      {c.tipSuffix}
    </>
  )

  const cards = [
    { title: c.card1Title, body: c.card1Body },
    { title: c.card2Title, body: c.card2Body },
    { title: c.card3Title, body: c.card3Body },
    { title: c.card4Title, body: c.card4Body },
  ]

  const renderCard = (card: { title: string; body: string }) => (
    <motion.div
      key={card.title}
      {...enterProps}
      transition={enterTransition}
      className="max-w-[250px]"
    >
      <h3 className="inline-block rounded-lg bg-slate-900 px-3 py-1 text-lg sm:text-xl font-semibold text-white">
        {card.title}
      </h3>
      <p className="mt-2 text-base sm:text-lg text-slate-600 leading-relaxed">{card.body}</p>
    </motion.div>
  )

  return (
    <section className="text-slate-900 min-h-[70vh] w-full flex flex-col justify-center pt-8">
      <div className="w-full max-w-4xl mx-auto space-y-8">
        {/* Title + intro, visible immediately. */}
        <div className="space-y-2 text-center">
          <h2 className="text-5xl sm:text-6xl font-semibold text-slate-900 tracking-tight whitespace-pre-line leading-[1.1]">
            {c.title}
          </h2>
          <p className="text-xl sm:text-2xl text-slate-600 leading-relaxed max-w-2xl mx-auto">
            {c.body}
          </p>
        </div>

        {/* Iceberg section */}
        <div className="relative mt-14">
          {/* Water-like gradient wash beneath the iceberg's waterline */}
          <motion.div
            {...enterProps}
            transition={enterTransition}
            className="pointer-events-none absolute left-1/2 w-screen -translate-x-1/2 top-[calc(38%-100px)] bottom-0 bg-gradient-to-b from-violet-200/50 via-violet-100/30 to-transparent"
            aria-hidden="true"
          />

          {/* Desktop: 3-column layout, cards flanking the iceberg */}
          <div className="relative hidden lg:grid gap-8 lg:grid-cols-[1fr_auto_1fr] items-start">
            <div className="space-y-10 pt-28 max-w-[250px]">
              {cards.slice(0, 2).map((card) => renderCard(card))}
            </div>

            <div className="flex flex-col items-center justify-center text-center">
              <div className="relative -translate-x-4">
                <span className="absolute -top-7 left-1/2 -translate-x-1/2 text-lg sm:text-xl font-semibold text-slate-400 whitespace-nowrap tabular-nums z-10">
                  {tipDisplay}
                </span>
                <motion.img
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 0.7 }}
                  transition={enterTransition}
                  src={iceburgImage}
                  alt={c.icebergAlt}
                  className="w-[320px] h-auto object-contain"
                  draggable={false}
                />
              </div>
            </div>

            <div className="space-y-10 pt-28 max-w-[250px]">
              {cards.slice(2, 4).map((card) => renderCard(card))}
            </div>
          </div>

          {/* Tablet & mobile: iceberg sits behind, cards flank in front */}
          <div className="relative lg:hidden">
            <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2">
              <div className="relative">
                <span className="absolute -top-6 left-1/2 -translate-x-1/2 text-base sm:text-lg font-semibold text-slate-400 whitespace-nowrap tabular-nums z-10">
                  {tipDisplay}
                </span>
                <motion.img
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 0.7 }}
                  transition={enterTransition}
                  src={iceburgImage}
                  alt=""
                  className="w-[200px] sm:w-[240px] h-auto object-contain"
                  draggable={false}
                />
              </div>
            </div>
            <div className="relative flex justify-between pt-8">
              <div className="space-y-8">
                {cards.slice(0, 2).map((card) => renderCard(card))}
              </div>
              <div className="space-y-8 text-end">
                {cards.slice(2, 4).map((card) => renderCard(card))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
